import type { RoundEvent } from "../../contracts/RoundSnapshot";
import type { ReplayContext } from "../core/ReplayContext";

/**
 * Event handlers can be:
 * - exact name handlers (eventTypeName matches)
 * - predicate handlers (canHandleEvent returns true)
 *
 * This is crucial for Hacksaw-style prefixed events like:
 * - fs_reveal, fs_2_reveal, fs_3_reveal
 * - fs_collapse, fs_2_collapse, ...
 */
export interface RoundEventHandler {
  /**
   * If provided, this handler is registered as an exact-match handler.
   */
  readonly eventTypeName?: string;

  /**
   * If provided, this handler can claim events dynamically (pattern-based).
   * Predicate handlers are evaluated only if no exact handler exists.
   */
  canHandleEvent?(roundEvent: RoundEvent): boolean;

  /**
   * When true, this exact-name handler may replace an already-registered handler for the same
   * eventTypeName during engine construction (the plugins' eventHandlers, then additionalEventHandlers).
   * Defaults to false — a collision then throws. Ignored for predicate handlers (they always
   * stack in registration order).
   */
  readonly override?: boolean;

  handleEvent(roundEvent: RoundEvent, context: ReplayContext): Promise<void>;
}

export class EventHandlerRegistry {
  private readonly handlersByTypeName: Map<string, RoundEventHandler> = new Map();
  private readonly predicateHandlers: RoundEventHandler[] = [];

  public registerHandler(handler: RoundEventHandler): void {
    if (handler.eventTypeName) {
      this.handlersByTypeName.set(handler.eventTypeName, handler);
      return;
    }

    if (handler.canHandleEvent) {
      this.predicateHandlers.push(handler);
      return;
    }

    throw new Error("RoundEventHandler must define either eventTypeName or canHandleEvent().");
  }

  public hasExactHandler(eventTypeName: string): boolean {
    return this.handlersByTypeName.has(eventTypeName);
  }

  public getHandler(roundEvent: RoundEvent): RoundEventHandler | undefined {
    // 1) exact match first
    const exact = this.handlersByTypeName.get(roundEvent.eventTypeName);
    if (exact) return exact;

    // 2) predicate matchers (in registration order)
    for (const handler of this.predicateHandlers) {
      if (handler.canHandleEvent && handler.canHandleEvent(roundEvent)) {
        return handler;
      }
    }

    return undefined;
  }
}